import { useState, useEffect } from 'react';
import flagsmith from '../lib/flagsmith';

export const useFeatureFlag = (flagName: string, defaultValue: boolean = false) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(defaultValue);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkFlag = () => {
      try {
        const flagValue = flagsmith.hasFeature(flagName);
        setIsEnabled(flagValue);
      } catch (error) {
        console.warn(`Failed to check feature flag ${flagName}:`, error);
        setIsEnabled(defaultValue);
      } finally {
        setLoading(false);
      }
    };

    // Check flag immediately
    checkFlag();

    // For now, we'll just check once. In production, you might want to set up polling
    // or use Flagsmith's real-time updates if available in your plan
  }, [flagName, defaultValue]);

  const refreshFlag = async () => {
    setLoading(true);
    try {
      await flagsmith.getFlags();
      const flagValue = flagsmith.hasFeature(flagName);
      setIsEnabled(flagValue);
    } catch (error) {
      console.warn(`Failed to refresh feature flag ${flagName}:`, error);
      setIsEnabled(defaultValue);
    } finally {
      setLoading(false);
    }
  };

  return { isEnabled, loading, refreshFlag };
};

export const useAllFeatureFlags = () => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const updateFlags = () => {
      try {
        const allFlags = flagsmith.getAllFlags();
        const flagsObject: Record<string, boolean> = {};
        
        // Handle the flags object structure
        Object.keys(allFlags).forEach((flagName) => {
          const flag = allFlags[flagName];
          flagsObject[flagName] = flag?.enabled || false;
        });
        
        setFlags(flagsObject);
      } catch (error) {
        console.warn('Failed to get all feature flags:', error);
      } finally {
        setLoading(false);
      }
    };

    updateFlags();
  }, []);

  const refreshFlags = async () => {
    setLoading(true);
    try {
      await flagsmith.getFlags();
      const allFlags = flagsmith.getAllFlags();
      const flagsObject: Record<string, boolean> = {};
      
      Object.keys(allFlags).forEach((flagName) => {
        const flag = allFlags[flagName];
        flagsObject[flagName] = flag?.enabled || false;
      });
      
      setFlags(flagsObject);
    } catch (error) {
      console.warn('Failed to refresh all feature flags:', error);
    } finally {
      setLoading(false);
    }
  };

  return { flags, loading, refreshFlags };
};
